/**
 * The header's account area: "Sign in" when nobody is signed in. Otherwise an "Admin" link for
 * the admin team and the account menu, which shows the signed-in wallet, its pages and
 * "Sign out".
 */

import { ChevronDown, LogOut } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

import { useSession, useSignOut } from "../auth/session.js";
import { accountLinks } from "./navigation.js";
import { SignInDialog } from "./SignInDialog.js";
import { CopyButton, styles } from "./ui.js";

/** Shortens an address for display: 0x8335…2913. */
export function shortAddress(address: string): string {
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

/** A small circle coloured from the address, so that a wallet is recognisable at a glance. */
export function WalletAvatar({
  address,
  className = "size-6",
}: {
  address: string;
  className?: string;
}) {
  const first = Number.parseInt(address.slice(2, 6), 16) % 360;
  const second = Number.parseInt(address.slice(-4), 16) % 360;
  return (
    <span
      aria-hidden
      className={`inline-block shrink-0 rounded-full ${className}`}
      style={{
        background: `linear-gradient(135deg, hsl(${first} 70% 55%), hsl(${second} 70% 42%))`,
      }}
    />
  );
}

export function AccountControls() {
  const session = useSession();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Nothing is shown until the session is known, so the header does not flash "Sign in" for
  // a signed-in user while the page loads.
  if (session.isPending) {
    return null;
  }

  if (session.data) {
    return (
      <div className="flex items-center gap-3">
        {session.data.isAdmin && (
          <Link
            to="/admin"
            className="rounded-full bg-brand-500/[0.08] px-2.5 py-1 text-xs font-semibold text-brand-800 ring-1 ring-brand-600/20 ring-inset transition-colors hover:bg-brand-500/15 dark:bg-brand-400/10 dark:text-brand-300 dark:ring-brand-400/25 dark:hover:bg-brand-400/20"
          >
            Admin
          </Link>
        )}
        <AccountMenu wallet={session.data.wallet} isAdmin={session.data.isAdmin} />
      </div>
    );
  }

  return (
    <>
      <button type="button" onClick={() => setDialogOpen(true)} className={styles.primaryButton}>
        Sign in
      </button>
      <SignInDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
}

/**
 * The signed-in wallet as a button that opens its menu. The menu closes on a choice, on Escape,
 * on a click elsewhere and when the page changes.
 */
function AccountMenu({ wallet, isAdmin }: { wallet: string; isAdmin: boolean }) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const signOut = useSignOut();
  const { pathname } = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: closes the menu on every page change.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onPointerDown(event: PointerEvent) {
      if (!container.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        button.current?.focus();
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={container} className="relative">
      <button
        ref={button}
        type="button"
        aria-label="Account menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 rounded-full bg-white py-1 pr-2.5 pl-1 text-sm font-medium text-zinc-900 shadow-sm ring-1 ring-zinc-900/10 transition-colors ring-inset hover:bg-zinc-50 hover:ring-zinc-900/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 aria-expanded:bg-zinc-50 dark:bg-white/[0.04] dark:text-zinc-100 dark:shadow-none dark:ring-white/10 dark:hover:bg-white/[0.08] dark:hover:ring-white/20 dark:aria-expanded:bg-white/[0.08]"
      >
        <WalletAvatar address={wallet} />
        <span className="font-mono">{shortAddress(wallet)}</span>
        <ChevronDown aria-hidden className="size-4 text-zinc-500 dark:text-zinc-400" />
      </button>
      {open && (
        <div
          id={menuId}
          className="absolute right-0 z-40 mt-2 w-64 rounded-2xl bg-white p-2 text-zinc-900 shadow-xl ring-1 shadow-zinc-900/10 ring-zinc-900/10 dark:bg-ink-850 dark:text-zinc-100 dark:shadow-black/50 dark:ring-white/10"
        >
          <div className="px-3 py-2">
            <p className="text-xs text-zinc-500 dark:text-zinc-400">Signed in with</p>
            <div className="mt-1 flex items-center justify-between gap-2">
              <span className="font-mono text-sm" title={wallet}>
                {shortAddress(wallet)}
              </span>
              <CopyButton value={wallet} label="Copy address" />
            </div>
          </div>
          <nav
            aria-label="Account"
            className="border-t border-zinc-900/[0.07] pt-1 dark:border-white/10"
          >
            <ul>
              {accountLinks(isAdmin).map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-zinc-700 transition-colors hover:bg-zinc-900/[0.05] hover:text-zinc-950 dark:text-zinc-300 dark:hover:bg-white/[0.06] dark:hover:text-white"
                  >
                    <Icon aria-hidden className="size-4 text-zinc-400" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <div className="mt-1 border-t border-zinc-900/[0.07] pt-1 dark:border-white/10">
            <button
              type="button"
              onClick={() => signOut.mutate()}
              disabled={signOut.isPending}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-zinc-700 transition-colors hover:bg-zinc-900/[0.05] hover:text-zinc-950 disabled:opacity-50 dark:text-zinc-300 dark:hover:bg-white/[0.06] dark:hover:text-white"
            >
              <LogOut aria-hidden className="size-4 text-zinc-400" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
